import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PurchaseNotifier } from '../components/PurchaseNotifier';
import { trackInitiateCheckout } from '../components/MetaPixel';
import { ArrowRight, CheckCircle2, ShieldCheck, Lock, HeartPulse, Gift } from 'lucide-react';
type Message = {
  id: string;
  sender: 'doc' | 'user' | 'card';
  text: string | React.ReactNode;
};
const DOC_AVATAR = 'https://cdn.magicpatterns.com/uploads/jHQ3ti2bdmP6k4iSE2nn2m/image.png';
export function LandingPageCanino9() {
  const [step, setStep] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [chatPhase, setChatPhase] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const checkoutUrl = 'https://pay.hotmart.com/Y104256235W?off=mstmo7g1&checkoutMode=10&bid=1773273486617';
  useEffect(() => {
    document.title = 'Diagnóstico de Alimentación | 30 Recetas Naturales';
  }, []);
  useEffect(() => {
    if (step === 1) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: 'smooth'
        });
      }, showOptions ? 250 : 100);
    }
  }, [messages, isTyping, step, showOptions]);
  const addDocMessage = (id: string, text: string | React.ReactNode) => {
    setMessages(prev => [...prev, {
      id,
      sender: 'doc',
      text
    }]);
  };
  const addCardMessage = (id: string, content: React.ReactNode) => {
    setMessages(prev => [...prev, {
      id,
      sender: 'card',
      text: content
    }]);
  };
  const sendDocSequence = (msgs: {
    id: string;
    text: string | React.ReactNode;
    isCard?: boolean;
  }[], startDelay: number = 1500, onComplete?: () => void) => {
    const timeouts: NodeJS.Timeout[] = [];
    let cumulativeTime = startDelay;
    msgs.forEach((msg, i) => {
      const msgText = typeof msg.text === 'string' ? msg.text : '';
      const typingDuration = msg.isCard ? 300 : Math.min(2500 + msgText.length * 6, 4500);
      const typingTimeout = setTimeout(() => {
        if (!msg.isCard) setIsTyping(true);
      }, cumulativeTime);
      timeouts.push(typingTimeout);
      cumulativeTime += typingDuration;
      const msgTimeout = setTimeout(() => {
        setIsTyping(false);
        if (msg.isCard) {
          addCardMessage(msg.id, msg.text);
        } else {
          addDocMessage(msg.id, msg.text);
        }
        if (i === msgs.length - 1 && onComplete) {
          setTimeout(onComplete, 1500);
        }
      }, cumulativeTime);
      timeouts.push(msgTimeout);
      if (i < msgs.length - 1) {
        const readingPause = msg.isCard ? 4000 : Math.max(3000, Math.min(msgText.length * 20, 8000));
        cumulativeTime += readingPause;
      }
    });
    return timeouts;
  };
  useEffect(() => {
    if (step !== 1) return;
    let timeouts: NodeJS.Timeout[] = [];
    if (chatPhase === 0 && messages.length === 0) {
      setIsTyping(true);
      const t1 = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-1', 'Hola 👋 Soy el Dr. Rennan Viegas. Voy a hacerte unas preguntas rápidas para analizar la alimentación de tu perro. Solo tomará 30 segundos.');
        const t2 = setTimeout(() => {
          setIsTyping(true);
        }, 1000);
        timeouts.push(t2);
        const t3 = setTimeout(() => {
          setIsTyping(false);
          addDocMessage('doc-2', 'Primero cuéntame, ¿qué edad tiene tu perro?');
          setShowOptions(true);
        }, 3500);
        timeouts.push(t3);
      }, 2500);
      timeouts.push(t1);
    }
    if (chatPhase === 1) {
      setShowOptions(false);
      setIsTyping(true);
      const t = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-3', 'Perfecto. Ahora dime, ¿qué tipo de comida consume normalmente?');
        setShowOptions(true);
      }, 2200);
      timeouts.push(t);
    }
    if (chatPhase === 2) {
      setShowOptions(false);
      setIsTyping(true);
      const t1 = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-4a', 'Entiendo. Muchos perros presentan problemas relacionados con su alimentación.');
        const t2 = setTimeout(() => {
          setIsTyping(true);
        }, 1000);
        timeouts.push(t2);
        const t3 = setTimeout(() => {
          setIsTyping(false);
          addDocMessage('doc-4b', '¿Tu perro tiene alguno de estos síntomas?');
          setShowOptions(true);
        }, 2800);
        timeouts.push(t3);
      }, 2000);
      timeouts.push(t1);
    }
    // Phase 3: Digestion question (NEW)
    if (chatPhase === 3) {
      setShowOptions(false);
      setIsTyping(true);
      const t1 = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-dig-1', 'Quiero entender mejor cómo está su digestión. ¿Has notado alguno de estos problemas?');
        setShowOptions(true);
      }, 2200);
      timeouts.push(t1);
    }
    // Phase 4: Energy question (NEW)
    if (chatPhase === 4) {
      setShowOptions(false);
      setIsTyping(true);
      const t1 = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-ener-1', 'Ahora dime algo importante sobre su energía diaria. ¿Cómo notas el nivel de energía de tu perro?');
        setShowOptions(true);
      }, 2200);
      timeouts.push(t1);
    }
    // Phase 5: Skin & coat question (NEW)
    if (chatPhase === 5) {
      setShowOptions(false);
      setIsTyping(true);
      const t1 = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-skin-1', 'La alimentación también afecta mucho la piel y el pelaje. ¿Has notado algo de esto?');
        setShowOptions(true);
      }, 2200);
      timeouts.push(t1);
    }
    // Phase 6: Analysis (was Phase 3)
    if (chatPhase === 6) {
      setShowOptions(false);
      setIsTyping(true);
      const t1 = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-5', 'Analizando la información sobre la alimentación de tu perro...');
        const t2 = setTimeout(() => {
          setIsTyping(true);
        }, 1500);
        timeouts.push(t2);
        const t3 = setTimeout(() => {
          setIsTyping(false);
          setChatPhase(7);
        }, 6500);
        timeouts.push(t3);
      }, 4000);
      timeouts.push(t1);
    }
    // Phase 7: Results + Emotional message (was Phase 4)
    if (chatPhase === 7) {
      const msgs = [{
        id: 'res-1',
        text: 'He terminado el análisis.'
      }, {
        id: 'res-2',
        text: 'Según tus respuestas, es posible que la alimentación actual de tu perro no esté cubriendo completamente sus necesidades nutricionales.'
      }, {
        id: 'res-3',
        text: 'Muchos perros que consumen alimentos poco balanceados comienzan a desarrollar problemas como alergias, problemas digestivos, caída de pelo o sobrepeso.'
      }, {
        id: 'res-4',
        text: 'Con el tiempo estos problemas empeoran y muchos dueños terminan gastando mucho dinero en veterinarios, medicamentos o tratamientos.'
      }, {
        id: 'res-5',
        text: 'Muchos dueños descubren demasiado tarde que la alimentación era una de las causas principales del deterioro en la salud de su perro.'
      }, {
        id: 'res-6',
        text: 'Déjame preguntarte algo importante… ¿Qué significa tu perro para ti?'
      }];
      const ts = sendDocSequence(msgs, 1500, () => {
        setShowOptions(true);
      });
      timeouts.push(...ts);
    }
    // Phase 8: Solution presentation (was Phase 5)
    if (chatPhase === 8) {
      setShowOptions(false);
      const productCard = <div className="bg-[#2F5D50] rounded-xl p-3 text-center" data-id="element-2321">
          <p className="text-[10px] font-bold text-green-300 uppercase tracking-wider mb-1" data-id="element-2322">
            Programa Clínico de Nutrición Natural para Perros
          </p>
          <div className="bg-white/10 rounded-lg p-2 mt-2 text-left" data-id="element-2323">
            <p className="text-white text-[11px] font-bold mb-1" data-id="element-2324">Incluye:</p>
            <ul className="text-white/90 text-[11px] space-y-1" data-id="element-2325">
              <li className="flex gap-1" data-id="element-2326">
                <CheckCircle2 className="w-3 h-3 text-green-300 flex-shrink-0 mt-0.5" data-id="element-2327" />{' '}
                30 recetas naturales balanceadas
              </li>
              <li className="flex gap-1" data-id="element-2328">
                <CheckCircle2 className="w-3 h-3 text-green-300 flex-shrink-0 mt-0.5" data-id="element-2329" />{' '}
                Recetas para cachorros, adultos y seniors
              </li>
              <li className="flex gap-1" data-id="element-2330">
                <CheckCircle2 className="w-3 h-3 text-green-300 flex-shrink-0 mt-0.5" data-id="element-2331" />{' '}
                Acceso inmediato y de por vida
              </li>
            </ul>
          </div>
        </div>;
      const bonosCard = <div className="bg-white border border-[#6DAA7F]/40 rounded-xl overflow-hidden" data-id="element-2332">
          <div className="bg-[#e8f5ec] px-3 py-2" data-id="element-2333">
            <p className="text-[11px] font-bold text-[#2F5D50] flex items-center gap-1" data-id="element-2334">
              <Gift className="w-3 h-3" data-id="element-2335" /> Regalos incluidos gratis:
            </p>
          </div>
          <div className="p-2.5 space-y-2" data-id="element-2336">
            <div className="flex items-start gap-2 bg-[#e8f5ec] p-2 rounded-lg" data-id="element-2337">
              <img src="https://cdn.magicpatterns.com/uploads/dhzCPguBEHAuB1vxaooPGU/ChatGPT_Image_5_feb_2026,_02_35_47_p.m..png" alt="Lista Negra + Lista Verde" className="w-10 h-10 object-cover rounded-md border border-[#6DAA7F] flex-shrink-0" data-id="element-2338" />
              <div data-id="element-2339">
                <p className="text-[11px] font-bold text-gray-900" data-id="element-2340">
                  REGALO #1: Lista Negra + Lista Verde
                </p>
                <p className="text-[10px] text-gray-600" data-id="element-2341">
                  Guía de alimentos seguros y prohibidos.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 bg-[#FFF3E0] p-2 rounded-lg" data-id="element-2342">
              <img src="https://cdn.magicpatterns.com/uploads/tmJCjjYa4iD56VcWj6sEtX/ChatGPT_Image_5_feb_2026,_12_53_16_p.m..png" alt="Plan Express 7 Días" className="w-10 h-10 object-cover rounded-md border border-[#F57C00] flex-shrink-0" data-id="element-2343" />
              <div data-id="element-2344">
                <p className="text-[11px] font-bold text-gray-900" data-id="element-2345">
                  REGALO #2: Plan Express 7 Días
                </p>
                <p className="text-[10px] text-gray-600" data-id="element-2346">
                  Transición segura a comida casera.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 bg-[#e8f5ec] p-2 rounded-lg" data-id="element-2347">
              <img src="https://cdn.magicpatterns.com/uploads/9pgdegNzbQ4opBiEgx4YKA/heylloyd-image-1769029546802.png" alt="Cómo Alargar la Vida de tu Perro" className="w-10 h-10 object-cover rounded-md border border-[#6DAA7F] flex-shrink-0" data-id="element-2348" />
              <div data-id="element-2349">
                <p className="text-[11px] font-bold text-gray-900" data-id="element-2350">
                  REGALO #3: Cómo Alargar la Vida de tu Perro
                </p>
                <p className="text-[10px] text-gray-600" data-id="element-2351">
                  Guía de hábitos diarios de salud.
                </p>
              </div>
            </div>
          </div>
        </div>;
      const sorteosContent = <div data-id="element-2352">
          <p className="font-bold text-[13px] mb-2" data-id="element-2353">🎁 Sorteos Especiales</p>
          <div className="space-y-1" data-id="element-2354">
            <p className="text-[13px]" data-id="element-2355">🎁 Cama para perro</p>
            <p className="text-[13px]" data-id="element-2356">🎁 Comedero para perro</p>
            <p className="text-[13px]" data-id="element-2357">🏥 Seguro veterinario SURA por 1 año</p>
          </div>
        </div>;
      const valueContent = <div data-id="element-2358">
          <p className="font-bold text-[13px] mb-2" data-id="element-2359">
            💰 Valor estimado real de todo este material:
          </p>
          <div className="space-y-1 text-[13px]" data-id="element-2360">
            <p data-id="element-2361">
              Consulta nutricional veterinaria:{' '}
              <span className="line-through text-gray-400" data-id="element-2362">$150.000</span>
            </p>
            <p data-id="element-2363">
              Plan alimenticio personalizado:{' '}
              <span className="line-through text-gray-400" data-id="element-2364">$300.000</span>
            </p>
            <p data-id="element-2365">
              Guías especializadas:{' '}
              <span className="line-through text-gray-400" data-id="element-2366">$150.000</span>
            </p>
            <div className="border-t border-dashed border-gray-300 pt-1.5 mt-1.5" data-id="element-2367">
              <p className="font-bold" data-id="element-2368">
                Valor total estimado:{' '}
                <span className="line-through text-gray-400" data-id="element-2369">$600.000+</span>
              </p>
            </div>
          </div>
        </div>;
      const msgs: {
        id: string;
        text: string | React.ReactNode;
        isCard?: boolean;
      }[] = [{
        id: 'sol-1',
        text: 'Eso es exactamente lo que sienten los dueños responsables.'
      }, {
        id: 'sol-2',
        text: 'La buena noticia es que mejorar la alimentación puede cambiar completamente la salud, energía y calidad de vida de un perro.'
      }, {
        id: 'sol-3',
        text: 'Durante los últimos 18 años he estudiado la nutrición canina y miles de dueños en Estados Unidos y Brasil han aplicado este método con resultados muy positivos.'
      }, {
        id: 'sol-4',
        text: 'Por eso creé un sistema simple para que cualquier dueño pueda preparar comida natural balanceada para su perro sin complicaciones.'
      }, {
        id: 'prod-card',
        text: productCard,
        isCard: true
      }, {
        id: 'sol-5',
        text: 'Además, hoy recibirás 3 regalos gratis para aplicar todo correctamente desde el primer día.'
      }, {
        id: 'bonos-card',
        text: bonosCard,
        isCard: true
      }, {
        id: 'sol-6',
        text: 'Y hoy también participas en sorteos especiales para dueños responsables.'
      }, {
        id: 'sorteos-msg',
        text: sorteosContent
      }, {
        id: 'value-msg',
        text: valueContent
      }, {
        id: 'sol-7',
        text: 'Pero solo por hoy puedes acceder al programa completo por solo $69.999 COP. Este precio es por tiempo limitado, mañana puede subir.'
      }, {
        id: 'sol-8',
        text: 'Si pudieras mejorar la alimentación y la salud de tu perro con una guía clara paso a paso… ¿te gustaría empezar hoy antes de que suba el precio?'
      }];
      const ts = sendDocSequence(msgs, 1500, () => {
        setShowOptions(true);
      });
      timeouts.push(...ts);
    }
    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [step, chatPhase]);
  const handleOptionClick = (text: string) => {
    setMessages(prev => [...prev, {
      id: `user-${Date.now()}`,
      sender: 'user',
      text
    }]);
    setChatPhase(prev => prev + 1);
  };
  const screenVariants = {
    initial: {
      opacity: 0,
      x: 20
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -20
    }
  };
  return <main className="h-[100dvh] md:min-h-screen md:h-auto bg-[#F7F8F7] flex flex-col items-center md:justify-center md:p-4 font-sans text-gray-900 relative overflow-hidden" data-id="element-2370">
      <div className="fixed inset-0 z-0 opacity-[0.03] hidden md:block" style={{
      backgroundImage: 'radial-gradient(#2F5D50 0.5px, transparent 0.5px)',
      backgroundSize: '24px 24px'
    }} data-id="element-2371" />
      <div className="w-full max-w-md bg-white md:rounded-[2rem] md:shadow-xl overflow-hidden relative h-full md:h-auto md:min-h-[650px] md:max-h-[90vh] flex flex-col md:border md:border-gray-200/60 z-10" data-id="element-2372">
        <div className="flex-1 relative flex flex-col min-h-0" data-id="element-2373">
          <AnimatePresence mode="wait" data-id="element-2374">
            {step === 0 && <motion.div key="step0" variants={screenVariants} initial="initial" animate="animate" exit="exit" transition={{
            duration: 0.3
          }} className="flex-1 flex flex-col items-center justify-center px-7 py-8 text-center relative overflow-hidden" style={{
            background: 'linear-gradient(165deg, #1a3a2a 0%, #2F5D50 35%, #3d7a68 65%, #2a6b5a 100%)'
          }} data-id="element-2375">
                <div className="absolute inset-0 opacity-[0.04]" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 18c-2.5-4-7-6-7-10s3-8 7-8 7 4 7 8-4.5 6-7 10zm-12 6c-4 2.5-6 7-10 7s-8-3-8-7 4-7 8-7 6 4.5 10 7zm24 0c4 2.5 6 7 10 7s8-3 8-7-4-7-8-7-6 4.5-10 7zm-12 12c0 5 3 9 3 9s-3 4-3 4-3-4-3-4 3-4 3-9z' fill='%23ffffff' fill-opacity='1'/%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px'
            }} data-id="element-2376" />
                <motion.div className="absolute top-6 right-6 w-20 h-20 rounded-full" style={{
              background: 'radial-gradient(circle, rgba(255,200,50,0.12) 0%, transparent 70%)'
            }} animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0.8, 0.5]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-2377" />
                <motion.div className="absolute bottom-12 left-4 w-16 h-16 rounded-full" style={{
              background: 'radial-gradient(circle, rgba(109,170,127,0.2) 0%, transparent 70%)'
            }} animate={{
              scale: [1, 1.3, 1],
              opacity: [0.4, 0.7, 0.4]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }} data-id="element-2378" />
                <div className="relative z-10 flex flex-col items-center" data-id="element-2379">
                  <div className="relative mb-5" data-id="element-2380">
                    <motion.div className="absolute -inset-2 rounded-full" style={{
                  background: 'conic-gradient(from 0deg, #F59E0B, #6DAA7F, #F59E0B, #6DAA7F, #F59E0B)'
                }} animate={{
                  rotate: 360
                }} transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-2381" />
                    <div className="relative w-36 h-36 rounded-full overflow-hidden border-[3px] border-white/90 shadow-xl" data-id="element-2382">
                      <img src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?q=80&w=400&auto=format&fit=crop" alt="Perro en evaluación nutricional" className="w-full h-full object-cover" data-id="element-2383" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-amber-400 to-amber-500 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 border-white" data-id="element-2384">
                      <HeartPulse className="w-5 h-5" data-id="element-2385" />
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300 bg-white/10 backdrop-blur-sm border border-white/15 px-4 py-2 rounded-full mb-5" data-id="element-2386">
                    <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" data-id="element-2387" />
                    Evaluación Nutricional Canina
                  </span>
                  <h1 className="text-[2rem] font-extrabold text-white mb-3 leading-tight tracking-tight" data-id="element-2388">
                    ¿Tu perro come <span className="text-amber-300" data-id="element-2389">bien</span>?
                  </h1>
                  <p className="text-white/70 text-[15px] mb-7 leading-relaxed max-w-[300px]" data-id="element-2390">
                    Descúbrelo en 30 segundos con una evaluación rápida basada
                    en nutrición veterinaria.
                  </p>
                  <button onClick={() => setStep(1)} className="w-full text-white font-bold text-lg py-4.5 rounded-xl shadow-xl flex items-center justify-center gap-2 active:scale-[0.97] transition-transform" style={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #F57C00 50%, #E65100 100%)',
                boxShadow: '0 6px 20px rgba(245,124,0,0.4), 0 2px 8px rgba(0,0,0,0.15)'
              }} data-id="element-2391">
                    Iniciar evaluación gratuita
                    <ArrowRight className="w-5 h-5" data-id="element-2392" />
                  </button>
                  <div className="mt-6 flex items-center justify-center gap-2.5" data-id="element-2393">
                    <div className="flex -space-x-2" data-id="element-2394">
                      {[11, 12, 13, 14, 15].map(i => <div key={i} className="w-7 h-7 rounded-full border-2 border-[#2F5D50] bg-gray-200 overflow-hidden shadow-sm" data-id="element-2395">
                          <img src={`https://i.pravatar.cc/100?img=${i}`} alt="Usuario" data-id="element-2396" />
                        </div>)}
                    </div>
                    <p className="text-[13px] text-white/70 font-medium" data-id="element-2397">
                      <strong className="text-amber-300" data-id="element-2398">+2.300</strong>{' '}
                      evaluaciones realizadas
                    </p>
                  </div>
                  <div className="mt-4 flex items-center justify-center gap-4 text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2399">
                    <div className="flex items-center gap-1" data-id="element-2400">
                      <ShieldCheck className="w-3.5 h-3.5" data-id="element-2401" /> Confidencial
                    </div>
                    <div className="flex items-center gap-1" data-id="element-2402">
                      <Lock className="w-3.5 h-3.5" data-id="element-2403" /> Seguro
                    </div>
                    <div className="flex items-center gap-1" data-id="element-2404">
                      <CheckCircle2 className="w-3.5 h-3.5" data-id="element-2405" /> Gratuito
                    </div>
                  </div>
                </div>
              </motion.div>}
            {step === 1 && <motion.div key="step1" variants={screenVariants} initial="initial" animate="animate" exit="exit" transition={{
            duration: 0.3
          }} className="flex-1 flex flex-col bg-[#f5f5f5] min-h-0" data-id="element-2406">
                <div className="bg-[#2F5D50] px-4 py-3 flex items-center gap-3 shadow-md z-10 flex-shrink-0" data-id="element-2407">
                  <div className="relative" data-id="element-2408">
                    <img src={DOC_AVATAR} alt="Dr. Rennan Viegas" className="w-10 h-10 rounded-full border-2 border-white/20 object-cover" data-id="element-2409" />
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-[#2F5D50] rounded-full" data-id="element-2410" />
                  </div>
                  <div data-id="element-2411">
                    <h2 className="text-white font-bold text-sm leading-tight" data-id="element-2412">
                      Dr. Rennan Viegas
                    </h2>
                    <p className="text-green-300 text-[10px] font-medium flex items-center gap-1" data-id="element-2413">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block" data-id="element-2414" />
                      En línea
                    </p>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-3 min-h-0" style={{
              WebkitOverflowScrolling: 'touch'
            }} data-id="element-2415">
                  <div className="text-center mb-3" data-id="element-2416">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-200/50 px-2 py-1 rounded-full" data-id="element-2417">
                      Hoy
                    </span>
                  </div>
                  {messages.map((msg, index) => {
                const isDoc = msg.sender === 'doc';
                const isCard = msg.sender === 'card';
                if (isCard) {
                  return <motion.div key={msg.id} initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="flex justify-start items-end gap-2" data-id="element-2418">
                          <div className="w-6 h-6 flex-shrink-0" data-id="element-2419" />
                          <div className="max-w-[85%]" data-id="element-2420">{msg.text}</div>
                        </motion.div>;
                }
                const showAvatar = isDoc && (index === 0 || messages[index - 1].sender !== 'doc');
                return <motion.div key={msg.id} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className={`flex ${isDoc ? 'justify-start' : 'justify-end'} items-end gap-2`} data-id="element-2421">
                        {isDoc && <div className="w-6 h-6 flex-shrink-0" data-id="element-2422">
                            {showAvatar && <img src={DOC_AVATAR} alt="Doc" className="w-full h-full rounded-full object-cover" data-id="element-2423" />}
                          </div>}
                        <div className={`max-w-[80%] p-3 text-[13px] leading-relaxed shadow-sm ${isDoc ? 'bg-white text-gray-800 rounded-2xl rounded-bl-sm border border-gray-100' : 'bg-[#2F5D50] text-white rounded-2xl rounded-br-sm'}`} data-id="element-2424">
                          {msg.text}
                        </div>
                      </motion.div>;
              })}
                  {isTyping && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="flex justify-start items-end gap-2" data-id="element-2425">
                      <div className="w-6 h-6 flex-shrink-0" data-id="element-2426">
                        <img src={DOC_AVATAR} alt="Doc" className="w-full h-full rounded-full object-cover" data-id="element-2427" />
                      </div>
                      <div className="bg-white p-3 rounded-2xl rounded-bl-sm border border-gray-100 shadow-sm flex items-center gap-1.5 h-10" data-id="element-2428">
                        <motion.div animate={{
                    y: [0, -4, 0]
                  }} transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    delay: 0
                  }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-2429" />
                        <motion.div animate={{
                    y: [0, -4, 0]
                  }} transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    delay: 0.15
                  }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-2430" />
                        <motion.div animate={{
                    y: [0, -4, 0]
                  }} transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    delay: 0.3
                  }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-2431" />
                      </div>
                    </motion.div>}
                  <div ref={messagesEndRef} data-id="element-2432" />
                </div>
                {showOptions && !isTyping && [0, 1, 2, 3, 4, 5, 7, 8].includes(chatPhase) && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="p-4 bg-white border-t border-gray-100 shadow-[0_-4px_10px_rgba(0,0,0,0.02)] flex-shrink-0" data-id="element-2433">
                      <p className="text-[10px] font-bold text-[#F57C00] uppercase tracking-wider mb-2 text-center animate-pulse" data-id="element-2434">
                        ⚡ Selecciona una respuesta para continuar
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center" data-id="element-2435">
                        {chatPhase === 0 && ['Cachorro (0-1 año)', 'Adulto (1-7 años)', 'Senior (+7 años)'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-full shadow-md hover:bg-[#245045] transition-colors active:scale-95" data-id="element-2436">
                              {opt}
                            </button>)}
                        {chatPhase === 1 && ['Concentrado comercial', 'Comida casera', 'Mezcla de ambos'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-full shadow-md hover:bg-[#245045] transition-colors active:scale-95" data-id="element-2437">
                              {opt}
                            </button>)}
                        {chatPhase === 2 && ['Alergias o picazón', 'Mal aliento', 'Caída excesiva de pelo', 'Sobrepeso', 'Ninguno'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-full shadow-md hover:bg-[#245045] transition-colors active:scale-95" data-id="element-2438">
                              {opt}
                            </button>)}
                        {chatPhase === 3 && ['Diarrea frecuente', 'Gases o pancita inflamada', 'Estreñimiento', 'Digestión normal'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-full shadow-md hover:bg-[#245045] transition-colors active:scale-95" data-id="element-2439">
                              {opt}
                            </button>)}
                        {chatPhase === 4 && ['Muy activo', 'Normal', 'Más cansado de lo normal', 'Duerme demasiado'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-full shadow-md hover:bg-[#245045] transition-colors active:scale-95" data-id="element-2440">
                              {opt}
                            </button>)}
                        {chatPhase === 5 && ['Pelo opaco', 'Caída excesiva de pelo', 'Piel seca', 'Todo normal'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-full shadow-md hover:bg-[#245045] transition-colors active:scale-95" data-id="element-2441">
                              {opt}
                            </button>)}
                        {chatPhase === 7 && ['Es parte de mi familia', 'Es mi compañero de vida', 'Lo amo y quiero lo mejor para él'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="w-full px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-xl shadow-md hover:bg-[#245045] transition-colors active:scale-95 text-left" data-id="element-2442">
                              {opt}
                            </button>)}
                        {chatPhase === 8 && ['Sí, quiero mejorar su alimentación', 'Quiero acceder al programa'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="w-full px-5 py-3 bg-gradient-to-r from-[#F57C00] to-[#E65100] text-white text-sm font-black rounded-xl shadow-lg hover:shadow-xl transition-all active:scale-95 text-left" data-id="element-2443">
                              {opt}
                            </button>)}
                      </div>
                    </motion.div>}
                {chatPhase >= 9 && !isTyping && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="p-4 bg-white border-t border-gray-100 shadow-[0_-4px_10px_rgba(0,0,0,0.02)] flex-shrink-0" data-id="element-2444">
                    <button onClick={() => setStep(2)} className="w-full bg-gradient-to-r from-[#F57C00] to-[#e07000] text-white font-black py-4 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 text-base active:scale-95" data-id="element-2445">
                      VER PROGRAMA RECOMENDADO
                      <ArrowRight className="w-5 h-5" data-id="element-2446" />
                    </button>
                    <div className="flex items-center justify-center gap-3 mt-2.5 text-[9px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-2447">
                      <div className="flex items-center gap-1" data-id="element-2448">
                        <CheckCircle2 className="w-3 h-3" data-id="element-2449" /> Pago único
                      </div>
                      <div className="flex items-center gap-1" data-id="element-2450">
                        <Lock className="w-3 h-3" data-id="element-2451" /> Acceso inmediato
                      </div>
                      <div className="flex items-center gap-1" data-id="element-2452">
                        <ShieldCheck className="w-3 h-3" data-id="element-2453" /> Acceso de por vida
                      </div>
                    </div>
                  </motion.div>}
              </motion.div>}

            {/* SCREEN 2: PRODUCT PAGE */}
            {step === 2 && <motion.div key="step2" variants={screenVariants} initial="initial" animate="animate" exit="exit" transition={{
            duration: 0.3
          }} className="flex-1 flex flex-col min-h-0 bg-white" data-id="element-2454">
                <div className="bg-[#2F5D50] px-4 py-3 text-center flex-shrink-0" data-id="element-2455">
                  <p className="text-[10px] font-bold text-green-300 uppercase tracking-wider" data-id="element-2456">
                    Tu programa recomendado
                  </p>
                  <p className="text-white font-extrabold text-sm mt-0.5" data-id="element-2457">
                    Programa Clínico de Nutrición Natural
                  </p>
                </div>
                <div className="flex-1 overflow-y-auto overscroll-contain" style={{
              WebkitOverflowScrolling: 'touch'
            }} data-id="element-2458">
                  <div className="p-4 space-y-4" data-id="element-2459">
                    <div className="rounded-xl overflow-hidden shadow-lg border border-gray-100" data-id="element-2460">
                      <img src="https://cdn.magicpatterns.com/uploads/gnEaoFwYpACgNWsD1np27d/ChatGPT_Image_10_mar_2026,_04_47_53_p.m..png" alt="Programa Clínico de Nutrición Natural - 30 Recetas Anti-Inflamación para Perros" className="w-full h-auto object-cover" data-id="element-2461" />
                    </div>
                    <div className="bg-[#2F5D50] rounded-xl p-4 text-white" data-id="element-2462">
                      <p className="text-[11px] font-bold text-green-300 uppercase tracking-wider mb-2" data-id="element-2463">
                        ¿Qué incluye el programa?
                      </p>
                      <ul className="space-y-2" data-id="element-2464">
                        {['30 recetas naturales balanceadas por veterinarios', 'Recetas para cachorros, adultos y seniors', 'Acceso inmediato y de por vida'].map((item, i) => <li key={i} className="flex items-start gap-2 text-[12px]" data-id="element-2465">
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-300 flex-shrink-0 mt-0.5" data-id="element-2466" />
                            <span className="text-white/90" data-id="element-2467">{item}</span>
                          </li>)}
                      </ul>
                    </div>
                    <div className="bg-white border border-[#6DAA7F]/40 rounded-xl overflow-hidden" data-id="element-2468">
                      <div className="bg-[#e8f5ec] px-4 py-2.5" data-id="element-2469">
                        <p className="text-[12px] font-bold text-[#2F5D50] flex items-center gap-1.5" data-id="element-2470">
                          <Gift className="w-4 h-4" data-id="element-2471" /> 3 Regalos incluidos
                          GRATIS
                        </p>
                      </div>
                      <div className="p-3 space-y-2.5" data-id="element-2472">
                        <div className="flex items-start gap-3 bg-[#e8f5ec] p-2.5 rounded-lg border border-[#6DAA7F]/20" data-id="element-2473">
                          <img src="https://cdn.magicpatterns.com/uploads/dhzCPguBEHAuB1vxaooPGU/ChatGPT_Image_5_feb_2026,_02_35_47_p.m..png" alt="Lista Negra + Lista Verde" className="w-12 h-12 object-cover rounded-lg border border-[#6DAA7F] flex-shrink-0" data-id="element-2474" />
                          <div data-id="element-2475">
                            <p className="text-[12px] font-bold text-gray-900" data-id="element-2476">
                              REGALO #1: Lista Negra + Lista Verde
                            </p>
                            <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-2477">
                              Guía de alimentos seguros y prohibidos para tu
                              perro.
                            </p>
                            <p className="text-[10px] font-semibold text-[#2F5D50] mt-0.5" data-id="element-2478">
                              Valor: $49.999 COP — Hoy: GRATIS
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 bg-[#FFF3E0] p-2.5 rounded-lg border border-[#F57C00]/20" data-id="element-2479">
                          <img src="https://cdn.magicpatterns.com/uploads/tmJCjjYa4iD56VcWj6sEtX/ChatGPT_Image_5_feb_2026,_12_53_16_p.m..png" alt="Plan Express 7 Días" className="w-12 h-12 object-cover rounded-lg border border-[#F57C00] flex-shrink-0" data-id="element-2480" />
                          <div data-id="element-2481">
                            <p className="text-[12px] font-bold text-gray-900" data-id="element-2482">
                              REGALO #2: Plan Express 7 Días
                            </p>
                            <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-2483">
                              Transición segura a comida casera sin problemas
                              digestivos.
                            </p>
                            <p className="text-[10px] font-semibold text-[#F57C00] mt-0.5" data-id="element-2484">
                              Valor: $49.999 COP — Hoy: GRATIS
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 bg-[#e8f5ec] p-2.5 rounded-lg border border-[#6DAA7F]/20" data-id="element-2485">
                          <img src="https://cdn.magicpatterns.com/uploads/9pgdegNzbQ4opBiEgx4YKA/heylloyd-image-1769029546802.png" alt="Cómo Alargar la Vida de tu Perro" className="w-12 h-12 object-cover rounded-lg border border-[#6DAA7F] flex-shrink-0" data-id="element-2486" />
                          <div data-id="element-2487">
                            <p className="text-[12px] font-bold text-gray-900" data-id="element-2488">
                              REGALO #3: Cómo Alargar la Vida de tu Perro
                            </p>
                            <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-2489">
                              Guía de hábitos diarios para mejorar su salud y
                              calidad de vida.
                            </p>
                            <p className="text-[10px] font-semibold text-[#2F5D50] mt-0.5" data-id="element-2490">
                              Valor: $49.999 COP — Hoy: GRATIS
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200/80 rounded-xl p-4" data-id="element-2491">
                      <p className="text-[12px] font-bold text-orange-800 mb-2 flex items-center gap-1.5" data-id="element-2492">
                        🎁 Sorteos Especiales (Solo compradores de hoy)
                      </p>
                      <div className="space-y-1.5" data-id="element-2493">
                        <p className="text-[12px] text-orange-700 flex items-center gap-2" data-id="element-2494">
                          🎁 Cama para perro
                        </p>
                        <p className="text-[12px] text-orange-700 flex items-center gap-2" data-id="element-2495">
                          🎁 Comedero para perro
                        </p>
                        <p className="text-[12px] text-orange-700 flex items-center gap-2" data-id="element-2496">
                          🏥 Seguro veterinario SURA por 1 año
                        </p>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-2497">
                      <p className="text-[12px] font-bold text-gray-800 mb-2" data-id="element-2498">
                        💰 Valor real de todo el material:
                      </p>
                      <div className="space-y-1.5 text-[12px] text-gray-600" data-id="element-2499">
                        <div className="flex justify-between" data-id="element-2500">
                          <span data-id="element-2501">Consulta nutricional veterinaria</span>
                          <span className="line-through text-gray-400" data-id="element-2502">
                            $150.000
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-2503">
                          <span data-id="element-2504">Plan alimenticio personalizado</span>
                          <span className="line-through text-gray-400" data-id="element-2505">
                            $300.000
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-2506">
                          <span data-id="element-2507">Guías especializadas (3)</span>
                          <span className="line-through text-gray-400" data-id="element-2508">
                            $150.000
                          </span>
                        </div>
                        <div className="border-t border-dashed border-gray-300 pt-2 mt-2 flex justify-between font-bold text-gray-800" data-id="element-2509">
                          <span data-id="element-2510">Valor total estimado</span>
                          <span className="line-through text-gray-400" data-id="element-2511">
                            $600.000+
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-xl p-4 text-center border-2 border-[#F57C00] relative overflow-hidden" style={{
                  background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 50%, #FFCC80 100%)'
                }} data-id="element-2512">
                      <p className="text-[10px] font-bold text-[#E65100] uppercase tracking-wider mb-1" data-id="element-2513">
                        🔥 Acceso completo hoy
                      </p>
                      <p className="text-3xl font-black text-[#E65100]" data-id="element-2514">
                        $69.999 <span className="text-sm" data-id="element-2515">COP</span>
                      </p>
                      <p className="text-[11px] text-[#F57C00] font-bold mt-1" data-id="element-2516">
                        Pago único · Acceso de por vida
                      </p>
                      <p className="text-[10px] text-red-600 font-bold mt-1.5 animate-pulse" data-id="element-2517">
                        ⏰ Precio sube pronto a $99.999
                      </p>
                    </div>

                    {/* Video Testimonial */}
                    <div className="rounded-xl overflow-hidden border border-gray-200" data-id="element-2518">
                      <div className="bg-[#2F5D50] px-3 py-2" data-id="element-2519">
                        <p className="text-[11px] font-bold text-white text-center" data-id="element-2520">
                          🎥 Ella lo comprobó con su perro
                        </p>
                      </div>
                      <video controls preload="auto" playsInline className="w-full h-auto" style={{
                    maxHeight: '400px'
                  }} data-id="element-2521">
                        <source src="https://ydgkmdmvhrniztrhtrxq.supabase.co/storage/v1/object/sign/audio%20receta/freepik_1080x1920_30fps_14228.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV80NDNkYTY1ZS1iYWM1LTQyYWQtODMzNS1iNWE4N2M0NDYxNmQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyByZWNldGEvZnJlZXBpa18xMDgweDE5MjBfMzBmcHNfMTQyMjgubXA0IiwiaWF0IjoxNzcxNzY2MDY4LCJleHAiOjE4MDMzMDIwNjh9.jJthctnqJad8IknGrrF_q9s4zxF0FqSFHDuaUtaZTD8#t=0.1" type="video/mp4" data-id="element-2522" />
                      </video>
                    </div>

                    {/* Testimonials */}
                    <div className="rounded-xl border border-gray-200 overflow-hidden" data-id="element-2523">
                      <div className="bg-[#f0f2f5] px-3 py-2 border-b border-gray-200" data-id="element-2524">
                        <p className="text-[11px] font-bold text-gray-700 text-center" data-id="element-2525">
                          💬 Lo que dicen otros dueños
                        </p>
                      </div>
                      <div className="p-3 space-y-3 bg-white" data-id="element-2526">
                        {[{
                      name: 'María García',
                      avatar: 'https://i.pravatar.cc/150?img=5',
                      content: 'Luna se rascaba hasta sangrar. Después de 3 semanas con estas recetas, su piel ya no está roja y su pelaje brilla como cuando era cachorra.',
                      dog: 'Luna - Golden Retriever, 7 años',
                      likes: 47
                    }, {
                      name: 'Carlos Rodríguez',
                      avatar: 'https://i.pravatar.cc/150?img=12',
                      content: 'Max siempre tenía heces blandas y muchos gases. Con estas recetas se arregló su digestión en días y tiene una energía que no había visto en años.',
                      dog: 'Max - Labrador, 5 años',
                      likes: 89
                    }, {
                      name: 'Ana Patricia López',
                      avatar: 'https://i.pravatar.cc/150?img=9',
                      content: 'Coco se lamía las patas por las alergias. Empecé con la receta de pescado y fue un milagro. Ya no se lame y duerme tranquilo toda la noche.',
                      dog: 'Coco - Beagle, 4 años',
                      likes: 156
                    }].map((t, i) => <div key={i} className="flex gap-2" data-id="element-2527">
                            <img src={t.avatar} alt={t.name} className="w-7 h-7 rounded-full object-cover flex-shrink-0" data-id="element-2528" />
                            <div className="flex-1" data-id="element-2529">
                              <div className="bg-[#f0f2f5] rounded-2xl px-2.5 py-2" data-id="element-2530">
                                <p className="font-semibold text-[11px] text-gray-900" data-id="element-2531">
                                  {t.name}
                                </p>
                                <p className="text-[11px] text-gray-700 mt-0.5 leading-relaxed" data-id="element-2532">
                                  {t.content}
                                </p>
                                <p className="text-[10px] text-gray-500 mt-1 bg-white inline-block px-1.5 py-0.5 rounded-full" data-id="element-2533">
                                  🐕 {t.dog}
                                </p>
                              </div>
                              <div className="flex items-center gap-3 mt-0.5 ml-2 text-[10px] text-gray-400" data-id="element-2534">
                                <span data-id="element-2535">👍 {t.likes}</span>
                              </div>
                            </div>
                          </div>)}
                      </div>
                      <div className="bg-[#f0f2f5] px-3 py-2 text-center border-t border-gray-200" data-id="element-2536">
                        <p className="text-[10px] text-gray-500" data-id="element-2537">
                          💬 Más de{' '}
                          <span className="font-bold text-gray-700" data-id="element-2538">
                            1,200 comentarios positivos
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Dr. Rennan Viegas Section */}
                    <div className="rounded-xl border border-[#6DAA7F]/40 overflow-hidden bg-white" data-id="element-2539">
                      <div className="bg-[#2F5D50] px-3 py-2" data-id="element-2540">
                        <p className="text-[11px] font-bold text-white text-center" data-id="element-2541">
                          🩺 Respaldo científico
                        </p>
                      </div>
                      <div className="p-4" data-id="element-2542">
                        <div className="rounded-lg overflow-hidden border border-gray-200 mb-4 shadow-sm" data-id="element-2543">
                          <img src="https://cdn.magicpatterns.com/uploads/wAzhSsfdxojeVHqwzkSjv6/image.png" alt="Perfil de Instagram del Dr. Rennan Viegas - 635 mil seguidores" className="w-full h-auto object-cover" data-id="element-2544" />
                        </div>
                        <div className="flex items-center gap-3 mb-3" data-id="element-2545">
                          <img src={DOC_AVATAR} alt="Dr. Rennan Viegas" className="w-12 h-12 rounded-full border-2 border-[#6DAA7F] object-cover flex-shrink-0" data-id="element-2546" />
                          <div data-id="element-2547">
                            <p className="text-[13px] font-bold text-[#2F5D50]" data-id="element-2548">
                              Dr. Rennan Viegas
                            </p>
                            <p className="text-[11px] text-[#6DAA7F] font-semibold" data-id="element-2549">
                              Médico Veterinario · +18 años
                            </p>
                          </div>
                        </div>
                        <p className="text-[12px] text-gray-700 leading-relaxed mb-3" data-id="element-2550">
                          Protocolo inspirado en la metodología del{' '}
                          <span className="font-bold text-[#2F5D50]" data-id="element-2551">
                            Dr. Rennan Viegas
                          </span>
                          , médico veterinario con más de 18 años de experiencia
                          en nutrición preventiva.
                        </p>
                        <p className="text-[11px] text-gray-500 leading-relaxed mb-3" data-id="element-2552">
                          Su enfoque ha ayudado a miles de dueños a mejorar la
                          salud digestiva de sus mascotas.
                        </p>
                        <div className="grid grid-cols-3 gap-2 bg-[#e8f5ec] rounded-lg p-3" data-id="element-2553">
                          <div className="text-center" data-id="element-2554">
                            <p className="text-[15px] font-black text-[#2F5D50]" data-id="element-2555">
                              +18
                            </p>
                            <p className="text-[9px] text-gray-500" data-id="element-2556">
                              años exp.
                            </p>
                          </div>
                          <div className="text-center border-x border-[#6DAA7F]/30" data-id="element-2557">
                            <p className="text-[15px] font-black text-[#2F5D50]" data-id="element-2558">
                              +635K
                            </p>
                            <p className="text-[9px] text-gray-500" data-id="element-2559">
                              seguidores
                            </p>
                          </div>
                          <div className="text-center" data-id="element-2560">
                            <p className="text-[15px] font-black text-[#2F5D50]" data-id="element-2561">
                              Miles
                            </p>
                            <p className="text-[9px] text-gray-500" data-id="element-2562">
                              pets ayudadas
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-white border-t border-gray-100 shadow-[0_-4px_10px_rgba(0,0,0,0.05)] flex-shrink-0" data-id="element-2563">
                  <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="block w-full" onClick={() => trackInitiateCheckout()} data-id="element-2564">
                    <button className="w-full bg-gradient-to-r from-[#F57C00] to-[#E65100] text-white font-black py-4 rounded-xl shadow-lg hover:shadow-xl transition-all flex flex-col items-center justify-center gap-0.5 text-base active:scale-95 animate-pulse" data-id="element-2565">
                      <span data-id="element-2566">🔥 ACCEDER AHORA — CUPOS LIMITADOS</span>
                      <span className="text-[11px] font-bold opacity-90" data-id="element-2567">
                        por $69.999 COP · Precio sube pronto a $99.999
                      </span>
                    </button>
                  </a>
                  <p className="text-[10px] text-red-500 font-bold text-center mt-2 animate-pulse" data-id="element-2568">
                    ⏰ Oferta válida solo por hoy — Quedan pocos cupos
                  </p>
                  <div className="flex items-center justify-center gap-3 mt-1.5 text-[9px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-2569">
                    <div className="flex items-center gap-1" data-id="element-2570">
                      <CheckCircle2 className="w-3 h-3" data-id="element-2571" /> Pago único
                    </div>
                    <div className="flex items-center gap-1" data-id="element-2572">
                      <Lock className="w-3 h-3" data-id="element-2573" /> Pago seguro
                    </div>
                    <div className="flex items-center gap-1" data-id="element-2574">
                      <ShieldCheck className="w-3 h-3" data-id="element-2575" /> Garantía
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
      <PurchaseNotifier enabled={step === 2} displayPrice="$69.999 COP" data-id="element-2576" />
    </main>;
}